import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import PasswordInput from '../ui/PasswordInput';
import { useNavigate } from 'react-router-dom';
import ForcePasswordChangeModal from './ForcePasswordChangeModal';
import ForceChangePinModal from './ForceChangePinModal';
import ForgotPinModal from './ForgotPinModal';

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [loginType, setLoginType] = useState('admin'); // 'admin' ou 'employee'
  const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);
  const [showForceChangePinModal, setShowForceChangePinModal] = useState(false);
  const [showForgotPinModal, setShowForgotPinModal] = useState(false);
  
  // Pour la connexion employé
  const [telephone, setTelephone] = useState('');
  const [pin, setPin] = useState('');

  const { login, employeeLogin, logout } = useAuth();
  const navigate = useNavigate();

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setRateLimitInfo(null);

    try {
      const userData = await login(email, password);
      
      // Réinitialiser le rate limit en cas de succès
      setRateLimitInfo(null);
      
      // Vérifier si le changement de mot de passe est obligatoire
      if (userData.must_change_password) {
        setShowForcePasswordChange(true);
      }
    } catch (error) {
      // Extraire les informations de rate limit depuis les headers
      const headers = error.response?.headers;
      if (headers) {
        const remaining = parseInt(headers['x-ratelimit-remaining'] || headers['X-RateLimit-Remaining']);
        const limit = parseInt(headers['x-ratelimit-limit'] || headers['X-RateLimit-Limit']);
        
        if (!isNaN(remaining) && !isNaN(limit)) {
          setRateLimitInfo({
            remaining: remaining,
            limit: limit,
            warning: remaining <= 2
          });
        }
      }
      
      // Message d'erreur
      const errorMessage = error.response?.data?.detail || 'Erreur de connexion';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChangeSuccess = () => {
    // Rediriger vers le dashboard après changement de mot de passe
    setShowForcePasswordChange(false);
    window.location.reload(); // Recharger pour mettre à jour le contexte
  };

  const handlePinChangeSuccess = () => {
    // Rediriger vers le dashboard après changement de PIN
    setShowForceChangePinModal(false);
    window.location.reload(); // Recharger pour mettre à jour le contexte
  };

  const handleEmployeeLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setRateLimitInfo(null);

    try {
      const userData = await employeeLogin(telephone, pin);
      
      // Réinitialiser le rate limit en cas de succès
      setRateLimitInfo(null);
      
      // Vérifier si le changement de PIN est obligatoire
      if (userData.must_change_password) { // Utilise must_change_password du token qui contient must_change_pin
        setShowForceChangePinModal(true);
      }
    } catch (error) {
      // Extraire les informations de rate limit depuis les headers
      const headers = error.response?.headers;
      if (headers) {
        const remaining = parseInt(headers['x-ratelimit-remaining'] || headers['X-RateLimit-Remaining']);
        const limit = parseInt(headers['x-ratelimit-limit'] || headers['X-RateLimit-Limit']);
        
        if (!isNaN(remaining) && !isNaN(limit)) {
          setRateLimitInfo({
            remaining: remaining,
            limit: limit,
            warning: remaining <= 2
          });
        }
      }
      
      // Message d'erreur
      const errorMessage = error.response?.data?.detail || 'Erreur de connexion';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" 
         style={{ 
           background: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 30%, #EC4899 60%, #F97316 100%)'
         }}>
      <div className="max-w-md w-full space-y-6">
        {/* Logo avec slogan */}
        <div className="text-center">
          <div className="flex flex-col items-center">
            <img 
              src="/logo-alo-advance.png" 
              alt="ALO ADVANCE" 
              className="h-40 w-40 object-contain drop-shadow-2xl"
            />
            <p className="text-white text-lg font-semibold italic -mt-12">Parce que Nous prévoyons Vos imprévus</p>
          </div>
        </div>

        {/* Sélecteur de type de connexion */}
        <div className="bg-white/10 backdrop-blur-sm p-1 rounded-xl border border-white/20">
          <div className="flex gap-2">
            <button
              type="button"
              className={`flex-1 py-3 px-4 text-sm font-semibold rounded-lg transition-all ${
                loginType === 'admin'
                  ? 'bg-white text-purple-600 shadow-lg'
                  : 'text-white hover:bg-white/10'
              }`}
              onClick={() => setLoginType('admin')}
            >
              Admin / Entreprise
            </button>
            <button
              type="button"
              className={`flex-1 py-3 px-4 text-sm font-semibold rounded-lg transition-all ${
                loginType === 'employee'
                  ? 'bg-white text-orange-600 shadow-lg'
                  : 'text-white hover:bg-white/10'
              }`}
              onClick={() => setLoginType('employee')}
            >
              Employé
            </button>
          </div>
        </div>

        {/* Card de connexion */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              {loginType === 'admin' ? 'Connexion Admin/Entreprise' : 'Connexion Employé'}
            </h2>

            {error && (
              <div className={`mb-4 p-4 border-l-4 rounded-r-lg ${
                rateLimitInfo?.warning 
                  ? 'bg-yellow-50 border-yellow-500 text-yellow-800' 
                  : rateLimitInfo?.remaining === 0
                  ? 'bg-red-100 border-red-600 text-red-800'
                  : 'bg-red-50 border-red-500 text-red-700'
              }`}>
                <div className="flex items-start">
                  <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                  </svg>
                  <div className="flex-1">
                    <p className="font-medium">{error}</p>
                    {rateLimitInfo && rateLimitInfo.remaining !== undefined && (
                      <div className="mt-2">
                        {rateLimitInfo.remaining > 0 ? (
                          <div className={`text-sm ${
                            rateLimitInfo.warning ? 'font-semibold' : ''
                          }`}>
                            <p>
                              {rateLimitInfo.warning && '⚠️ '}
                              <strong>{rateLimitInfo.remaining}</strong> tentative{rateLimitInfo.remaining > 1 ? 's' : ''} restante{rateLimitInfo.remaining > 1 ? 's' : ''}
                            </p>
                            {rateLimitInfo.warning && (
                              <p className="mt-1 text-xs">
                                Attention : Vous serez bloqué temporairement après la dernière tentative.
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm font-semibold">
                            🚫 Compte temporairement bloqué. Réessayez dans 60 secondes.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {loginType === 'admin' ? (
              <form onSubmit={handleAdminLogin} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Adresse email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                    Mot de passe
                  </label>
                  <PasswordInput
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3"
                    required
                  />
                </div>
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm font-medium text-purple-600 hover:text-orange-600 transition"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
                <Button
                  type="submit"
                  className="w-full py-3 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 hover:from-purple-700 hover:via-pink-600 hover:to-orange-600 text-white font-semibold rounded-lg shadow-lg transition-all transform hover:scale-105"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Connexion en cours...
                    </span>
                  ) : 'Se connecter'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleEmployeeLogin} className="space-y-5">
                <div>
                  <label htmlFor="telephone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Numéro de téléphone
                  </label>
                  <Input
                    id="telephone"
                    type="tel"
                    value={telephone}
                    onChange={(e) => setTelephone(e.target.value)}
                    placeholder="224 XX XX XX XX"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="pin" className="block text-sm font-semibold text-gray-700 mb-2">
                    Code PIN (4 chiffres)
                  </label>
                  <PasswordInput
                    id="pin"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    placeholder="••••"
                    maxLength="4"
                    className="w-full px-4 py-3"
                    required
                  />
                </div>
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => setShowForgotPinModal(true)}
                    className="text-sm font-medium text-purple-600 hover:text-orange-600 transition"
                  >
                    PIN oublié ?
                  </button>
                </div>
                <Button
                  type="submit"
                  className="w-full py-3 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 hover:from-purple-700 hover:via-pink-600 hover:to-orange-600 text-white font-semibold rounded-lg shadow-lg transition-all transform hover:scale-105"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Connexion en cours...
                    </span>
                  ) : 'Se connecter'}
                </Button>
              </form>
            )}

            {/* Identifiants de démonstration supprimés pour la production */}

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Pas encore inscrit ?{' '}
                <button
                  type="button"
                  className="text-purple-600 hover:text-orange-600 font-semibold transition"
                  onClick={() => navigate('/register')}
                >
                  Créer un compte
                </button>
              </p>
            </div>
          </div>
        </div>

        {/* Signature ALO FINANCES */}
        <div className="text-center mt-8">
          <p className="text-white text-sm font-light">
            by <span className="font-semibold">ALO FINANCES</span>
          </p>
          <p className="text-blue-200 text-xs mt-1">
            © 2025 Tous droits réservés
          </p>
        </div>
      </div>

      {/* Modal de changement forcé de mot de passe */}
      {showForcePasswordChange && (
        <ForcePasswordChangeModal onSuccess={handlePasswordChangeSuccess} />
      )}
      
      {/* Modal de changement forcé de PIN employé */}
      {showForceChangePinModal && (
        <ForceChangePinModal onSuccess={handlePinChangeSuccess} />
      )}
      
      {/* Modal PIN oublié */}
      <ForgotPinModal 
        isOpen={showForgotPinModal} 
        onClose={() => setShowForgotPinModal(false)} 
      />
    </div>
  );
};

export default LoginForm;